import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { StatusCode } from "api/protocols";

import { ICustomer, ICustomerPreferences } from "redux/reducers/models/customer";

import { useCustomerContext } from "../context/CustomerContext";

import CustomerTabs from "../tabs/CustomerTabs";
import CustomerImage from "elements/customer/CustomerImage";

import "./customer.scss";
import Sheet from "components/sheet/Sheet";
import GolferCard from "components/bookingPopUp/golferCard/GolferCard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Select } from "components/select/index";
import axios, { CancelToken } from "axios";
import Popup from "components/popup/Popup";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import { useTranslation } from "react-i18next";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import {
  DeleteCustomer,
  DeleteCustomerProfileImage,
  GetCustomer,
  GetCustomerAuditTrail,
  PutBanCustomer,
  PutMergeCustomers,
  SendPasswordReset,
  SendWelcomeEmail,
  UploadCustomerProfileImage,
} from "api/rpc/2024-04/facilityAdmin/customer/customer";
import PreferenceCard from "components/preferenceCard/PreferenceCard";
import useModal from "hooks/modals/useModal";
import Portal from "elements/Portal";
import { faBan, faCircleCheck, faCircleExclamation, faTrash } from "@fortawesome/pro-regular-svg-icons";
import moment, { MomentInput } from "moment";
import Callout from "components/callout/Callout";
import TextField from "components/form/textField/TextField";


interface IMergeState {
  //Merge Modal
  showMergeModal: boolean;
  selectedCustomer: ICustomer;
  customerSearchResults: Array<ICustomer>;
  customerSearchQuery: string;
  customerSearching: boolean;

  //Warning Popup
  showWarning: boolean;
}

/**
 * Customer Profile 'landing' page.
 *   - Wrapped in a Customer Context to handle persistent data across Tab navigation.
 *   - Only renders after the context has loaded and set the context state.
 */
export default function Customer() {
  const { state, updateState, loadCustomer } = useCustomerContext();

  const { Option } = Select;
  const dispatch = useAppDispatch();
  const { authStore } = useAppSelector(store => store as { authStore: { user: { permissions: { customers_ban_account: boolean } } } });
  const history = useHistory();
  const { t } = useTranslation();

  const [welcomeEmailVisible, setWelcomeEmailVisible] = useState<boolean>(false);
  const [preferencesVisible, setPreferencesVisible] = useState<boolean>(false);
  const [resetPasswordVisible, setResetPasswordVisible] = useState<boolean>(false);
  const [prevPreferences, setPrevPreferences] = useState<Partial<ICustomerPreferences>>(undefined); // Default preferences on load

  const { state: banModal, updateModal: updateBanModal, closeModal: closeBanModal } = useModal({ reason: "" });
  const { state: unbanModal, updateModal: updateUnBanModal, closeModal: closeUnBanModal } = useModal();
  const {
    state: banDetailsModal,
    updateModal: updateBanDetailsModal,
    closeModal: closeBanDetailsModal,
  } = useModal({ reason: "", edit: false });

  const [mergeState, setMergeState] = useState<IMergeState>({
    showMergeModal: false,
    selectedCustomer: null,
    customerSearchResults: [],
    customerSearchQuery: "",
    customerSearching: false,

    showWarning: false,
  });

  const [auditTrail, setAuditTrail] = useState<Array<any>>([]);

  const { state: auditModal, updateModal: updateAuditModal, closeModal: closeAuditModal } = useModal({ isOpen: false });


  const [deleteState, setDeleteState] = useState({
    showDeleteModal: false,
  });

  useEffect(() => {
    if (state.preferences) {
      setPrevPreferences({ ...state.preferences });
    }
  }, [state?.customer?.id]);

  useEffect(() => {
    let mounted = true;
    let timeoutId: NodeJS.Timeout = null;
    const source = axios.CancelToken.source();
    const search = () => {
      timeoutId = global.setTimeout(() => {
        void (async () => {
          if (mergeState.customerSearchQuery !== "") {
            try {
              setMergeState(prevState => ({ ...prevState, customerSearching: true }));
              const customers = await searchCustomer(source.token);
              if (mounted) {
                setMergeState(prevState => ({
                  ...prevState,
                  customerSearchResults: customers,
                  customerSearching: false,
                }));
              }
            } catch (error) {
              console.log("err", error);
            }
            return;
          } else {
            setMergeState(prevState => ({ ...prevState, customerSearchResults: [], customerSearching: false }));
          }
        })();
      }, 1000);
    };
    search();
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      source.cancel();
    };
  }, [mergeState.customerSearchQuery]);

  useEffect(() => {
    if (!auditModal.isOpen) {
      return;
    }
    void getAuditTrail();
  }, [auditModal.isOpen === true]);

  async function searchCustomer(cancelToken: CancelToken) {
    const customerRes = await GetCustomer({ search: mergeState.customerSearchQuery }, false, cancelToken);
    if (customerRes.status !== StatusCode.OK) {
      return [];
    }
    const filteredCustomers = customerRes?.data?.filter(customer => customer?.id !== state?.customer?.id);
    return filteredCustomers;
  }

  async function saveCustomerImage(imageFile: File) {
    const formData = new FormData();

    formData.append("profile_image", imageFile);
    formData.append("id", state.customer.id.toString());

    const imageRes = await UploadCustomerProfileImage(formData, true);
    if (imageRes.status !== StatusCode.OK) {
      return;
    }

    console.log(imageRes.data); //debug

    const tempCustomer: ICustomer = { ...state.customer, ...imageRes.data };
    updateState({ customer: tempCustomer });
  }

  async function deleteCustomerImage() {
    const imageRes = await DeleteCustomerProfileImage({ id: state.customer.id }, true);
    if (imageRes.status !== StatusCode.OK) {
      return;
    }

    console.log("imageUpdate", imageRes.data); //debug

    const tempCustomer: ICustomer = { ...state.customer, ...imageRes.data };
    updateState({ customer: tempCustomer });
  }

  function handleToggleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { id, checked } = event.target;
    updateState({ preferences: { ...state.preferences, [id]: checked } });
  }

  async function handleSendWelcomeEmail() {
    const sendWelcomeRes = await SendWelcomeEmail({ email: state.editState.email }, true);

    if (sendWelcomeRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error sending welcome email")));
      return;
    }

    setWelcomeEmailVisible(false);
  }

  /** Called after POPUP to reset customer password */
  async function sendPasswordReset() {
    const params = {
      email: state.customer.email,
    };

    const sendPasswordResetRes = await SendPasswordReset(params, true);

    if (sendPasswordResetRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error sending password reset email")));
      return;
    }

    setResetPasswordVisible(false);
    dispatch(showSuccess(t("Password reset email sent successfully")));
  }

  // async function handleSavePreferences() {
  //   const preferencesRes = await PutCustomerPreferences(
  //     {
  //       ...state.preferences,
  //     },
  //     true,
  //   );

  //   if (preferencesRes.status !== StatusCode.OK) {
  //     dispatch(showError(t("Error saving preferences")));
  //     return;
  //   }

  //   ReactDOM.unstable_batchedUpdates(() => {
  //     setPreferencesVisible(false);
  //     setPrevPreferences({ ...state.preferences });
  //   });
  //   dispatch(showSuccess(t("Preferences saved successfully")));
  // }

  function closeMergeAccounts() {
    setMergeState(prevState => ({
      ...prevState,
      showMergeModal: false,
      customerSearchQuery: "",
      customerSearchResults: [],
      selectedCustomer: null,
      showWarning: false,
    }));
  }

  async function handleBanCustomer(type: "ban" | "edit" | "unban") {
    const customerRes = await PutBanCustomer(
      {
        id: state.customer.id,
        banned: type === "ban" || type === "edit" ? true : false,
        banned_reason: type === "ban" ? banModal?.reason : type === "edit" ? banDetailsModal?.reason : null,
      },
      true,
    );

    if (customerRes?.status !== StatusCode?.OK) {
      dispatch(showError(t("Error banning customer")));
      return;
    }
    void closeBanModal();
    void closeBanDetailsModal();
    void closeUnBanModal();
    switch (type) {
      case "ban":
        dispatch(showSuccess(t("Successfully banned customer")));
        break;
      case "edit":
        dispatch(showSuccess(t("Successfully updated ban")));
        break;
      case "unban":
        dispatch(showSuccess(t("Successfully unbanned customer")));
        break;
      default:
        dispatch(showSuccess(t("Successfully banned customer")));
        break;
    }
    await loadCustomer(state?.customer?.id);
  }

  async function handleCustomerMerge() {
    const mergeRes = await PutMergeCustomers(
      { parent_id: state?.customer?.id, child_id: mergeState?.selectedCustomer?.id },
      true,
    );
    if (mergeRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error merging customer accounts")));
      return;
    }
    dispatch(showSuccess(t("Successfully merged customer accounts")));
    void closeMergeAccounts();
    await loadCustomer(state?.customer?.id);
  }

  async function getAuditTrail() {
    const auditRes = await GetCustomerAuditTrail({ customer_id: state?.customer?.id }, true);

    if (auditRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error fetching audit trail")));
      return;
    }
    setAuditTrail(auditRes.data as Array<any>);
  }

  function handleCustomerSearch(query: string) {
    setMergeState(prevState => ({ ...prevState, customerSearchQuery: query }));
  }

  function handleCustomerSelection(customer: ICustomer) {
    setMergeState(prevState => ({ ...prevState, selectedCustomer: customer }));
  }

  async function handleDeleteRequest() {
    const deleteCustomerRes = await DeleteCustomer({ id: state?.customer?.id }, true);
    if (deleteCustomerRes.status !== StatusCode.OK) {
      dispatch(showError(String(deleteCustomerRes.data.message)));
      return;
    }
    dispatch(showSuccess(t("User successfully deleted")));
    history.push(`/admin/customers`);
  }

  function renderCustomerStatsGroup(screenSize: "desktop" | "mobile") {
    return (
      <div className={"booking-stats-general"}>
        <div className={`customer-stats-group-${screenSize}`}>
          {screenSize === "desktop" && <span className="customer-stats-group-desktop-empty-space" />}
          <div className={`customer-dropdown-${screenSize}`}>
            <NavigationDropdownNew
              rightAlign
              label={t("Settings")}
              sections={[
                [
                  {
                    label: t("Customer Preferences"),
                    onClick: () => setPreferencesVisible(true),
                    icon: "eye",
                  },
                ],
                [
                  {
                    label: t("Password Reset"),
                    onClick: () => setResetPasswordVisible(true),
                    icon: "lock-a",
                  },
                ],
                [
                  {
                    label: t("Send Welcome Email"),
                    onClick: () => setWelcomeEmailVisible(true),
                    icon: "send",
                  },
                ],
                [
                  {
                    label: t("Ban Account"),
                    onClick: () => updateBanModal({ isOpen: true }),
                    icon: "ban",
                    disabled: state?.customer?.banned || !authStore?.user?.permissions?.customers_ban_account,
                    hidden: state?.customer?.banned,
                  },
                  {
                    label: t("Edit Ban"),
                    onClick: () =>
                      updateBanDetailsModal({ isOpen: true, edit: true, reason: state?.customer?.banned_reason }),
                    icon: "ban",
                    disabled: !state?.customer?.banned || !authStore?.user?.permissions?.customers_ban_account,
                    hidden: !state?.customer?.banned,
                  },
                  {
                    label: t("Unban Account"),
                    onClick: () => updateUnBanModal({ isOpen: true }),
                    icon: "lock-open",
                    disabled: !state?.customer?.banned || !authStore?.user?.permissions?.customers_ban_account,
                    hidden: !state?.customer?.banned,
                  },
                ],
                [
                  {
                    label: t("Merge Customer"),
                    onClick: () => setMergeState(prevState => ({ ...prevState, showMergeModal: true })),
                    icon: "copy",
                  },
                ],
                [
                  {
                    label: t("Audit Trail"),
                    onClick: () => {
                      updateAuditModal({ isOpen: true });
                    },
                    icon: "history",
                  },
                ],
                [
                  {
                    label: t("Delete Customer"),
                    onClick: () =>
                      setDeleteState(prevState => ({ ...prevState, showDeleteModal: !prevState.showDeleteModal })),
                    icon: "trash",

                  },
                ],
              ]}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {state.customer && (
        <>
          <div>
            {state?.customer?.banned ? (
              <>
                <Callout
                  type="error"
                  title={t("Account Banned")}
                  actions={{
                    primary: { label: t("View details"), onClick: () => updateBanDetailsModal({ isOpen: true }) },
                  }}
                  content={`Banned on ${moment.utc(state?.customer?.banned_at).local()?.format("LLL")} by ${
                    state?.customer?.banned_by_user_full_name
                  }`}
                />
                <br />
              </>
            ) : null}
            {/* Customer STATS */}
            <div className="customer_stats_bar">
              <div className="stats_bar-picture">
                <CustomerImage
                  imageSource={state.customer.profile_image_source}
                  saveProfileImage={imageFile => void saveCustomerImage(imageFile)}
                  deleteProfileImage={() => void deleteCustomerImage()}

                />
              </div>

              <div className="stats-general">
                <div className="customer-name-container">
                  <h1 className="display-md text-semibold">{state.customer.full_name}</h1>
                </div>

                <span className="customer-types">
                  <p className="customer-membership">{state.customer.customer_type}</p>
                  <p className="customer-membership">{state.customer.cart_customer_type}</p>
                </span>

                <div className="customer-contact">
                  {state.customer.email ? (
                    <div>{state.customer.email}</div>
                  ) : (
                    <div className="field-unavailable">No Email On File</div>
                  )}
                </div>
                <div className="customer-contact-phone-number">
                  {state.customer.phone ? (
                    <p>{state.customer.phone}</p>
                  ) : (
                    <p className="field-unavailable" style={{ marginLeft: "0px" }}>
                      {t("No Phone Number")}
                    </p>
                  )}
                </div>
              </div>
              {renderCustomerStatsGroup("desktop")}
            </div>
            <div className="customer-dropdown-mobile">{renderCustomerStatsGroup("mobile")}</div>
          </div>
          <CustomerTabs customerId={state.customer.id} />

          {/* RESET PASSWORD POPUP */}
          <Popup
            open={resetPasswordVisible}
            description={`${t("An email will be sent to reset the customer's password")}`}
            title={t("Reset Password?")}
            type="info"
            onCancel={() => setResetPasswordVisible(false)}
            onOk={sendPasswordReset}
          />

          {/* SEND WELCOME EMAIL POPUP */}
          <Popup
            open={welcomeEmailVisible}
            description={t("Send new user a welcome email")}
            title={t("Send Welcome Email")}
            type="info"
            onCancel={() => setWelcomeEmailVisible(false)}
            onOk={handleSendWelcomeEmail}
          />

          {/* CUSTOMER PREFERENCES SHEET */}
          <Sheet
            open={preferencesVisible}
            size="medium"
            closable
            title={t("Customer Preferences")}
            onCancel={() => {
              setPreferencesVisible(false);
              updateState({ preferences: prevPreferences ? prevPreferences : state.preferences });
            }}
          >
            <div>
              <div>
                <span className="portal-preferences-header">Notifications Settings</span> 
                <p className="text-subdued">
                  {
                    t(
                      "We may still send you important notifications about your account outside of your normal notification settings",
                    ) 
                  }
                </p>
              </div>
              <div className="portal-preferences-divider" />
              {/* <PreferenceCard
                title={t("Marketing Notifications")} 
                description={t("Information about upcoming promotions")} 
                handleToggleChange={handleToggleChange}
                options={[
                 {
                  label: t("Email"), 
                  id: "marketing_email",
                  checked: state.preferences?.marketing_email,
                  disabled: true,
                 },
                ]}
              /> */}

              <PreferenceCard
                title={t("Booking Confirmations")}
                description={t("Notifications when you create a new booking")}
                handleToggleChange={handleToggleChange}
                options={[
                  {
                    label: t("Email"),
                    id: "booking_confirmation_email",
                    checked: state.preferences?.booking_confirmation_email,
                    disabled: true,
                  },
                ]}
              />
              {/* <PreferenceCard
                title={t("Booking Reminders")}
                description={t("Notifications when you have an upcoming booking")}
                handleToggleChange={handleToggleChange}
                options={[
                  {
                    label: t("Email"),
                    id: "booking_reminder_email",
                    checked: state.preferences?.booking_reminder_email,
                    disabled: true,
                  },
                ]}
              /> */}

              {/* <PreferenceCard
                  title={t("Booking Updates")} 
                  description={t("Notifications when you update an upcoming booking")}
                  handleToggleChange={handleToggleChange}
                  options={[
                    {
                      label: t("Email"), 
                      id: "booking_update_email",
                      checked: state.preferences?.booking_update_email,
                      disabled: true,
                    },
                  ]}
                /> */}
              <PreferenceCard
                title={t("Booking Cancellations")}
                description={t("Notifications when you cancel an upcoming booking")}
                handleToggleChange={handleToggleChange}
                options={[
                  {
                    label: t("Email"),
                    id: "booking_cancellation_email",
                    checked: state.preferences?.booking_cancellation_email,
                    disabled: true,
                  },
                ]}
              />
              <PreferenceCard
                title={t("Display Name on Tee Sheet")}
                description={t(
                  "Display your name on tee sheet on online booking portal. Other users will be able to see your name when booking tee times on the tee sheet",
                )}
                handleToggleChange={handleToggleChange}

                options={[
                  {
                    label: t("Display"),
                    id: "display_name_on_tee_sheet",
                    checked: state?.preferences?.display_name_on_tee_sheet,
                    disabled: true,
                  },
                ]}
              />
            </div>
          </Sheet>

          <Popup
            open={deleteState.showDeleteModal}
            description={t("Are you sure you want to delete this customer? This is a permanent decision")}
            title={t("Delete Customer")}
            type="warning"
            onCancel={() => setDeleteState(prevState => ({ prevState, showDeleteModal: !prevState.showDeleteModal }))}
            onOk={handleDeleteRequest}
          />
          <Sheet
            title={t("Merge Customer Accounts")}
            open={mergeState.showMergeModal}
            size="small"
            closable
            onCancel={closeMergeAccounts}
            onOk={() => setMergeState(prevState => ({ ...prevState, showWarning: true }))}
            okText={t("Continue")}
            okDisabled={!mergeState.selectedCustomer ? true : false}
            overflow
          >
            <span className="merge-customer-title">{t("Customer To Merge")}</span>
            {mergeState.selectedCustomer ? (
              <GolferCard
                closable
                removeGolfer={() =>
                  setMergeState(prevState => ({
                    ...prevState,
                    selectedCustomer: null,
                    customerSearchResults: [],
                    customerSearchQuery: "",
                  }))
                }
                email={mergeState?.selectedCustomer?.email}
                name={mergeState?.selectedCustomer?.full_name}
                memberCode={mergeState?.selectedCustomer?.member_code}
                customerType={mergeState?.selectedCustomer?.customer_type}
                phone={mergeState?.selectedCustomer?.phone}
              />
            ) : (
              <>
                <Select
                  showSearch
                  onSearch={(query: string) => handleCustomerSearch(query)}
                  onChange={(id: number, customer: ICustomer) => handleCustomerSelection(customer)}
                  placeholder={t("Search...")}
                  allowClear
                  searchValue={mergeState.customerSearchQuery}
                  showDropDownOnFocus={true}
                  searching={mergeState.customerSearching}
                  noData={
                    <div className="ui-select-dropdown-list-item">
                      <span>{t("No users found...")}</span>
                      <FontAwesomeIcon style={{ marginLeft: "5px" }} icon={["far", "users-slash"]} size="1x" />
                    </div>
                  }
                >
                  {mergeState.customerSearchResults?.map((customer, index) => {
                    return (
                      <Option key={index} value={customer.id} name={customer.full_name} extraValues={customer}>
                        <div className="flex justify-between">
                          <div>
                            <div className="text-semibold text-lg">{customer?.full_name}</div>
                            <div className="text-sm text-gray-500">{customer.customer_type}</div>
                            <div className="text-sm text-gray-500">{customer.email}</div>
                            <div className="text-sm text-gray-500">{customer.phone ? customer.phone : null}</div>
                          </div>

                          <div className="font-medium text-base text-gray-500 self-end">{customer.member_code}</div>
                        </div>
                      </Option>
                    );
                  })}
                </Select>
              </>
            )}
            {state.customer !== null ? (
              <div>
                <FontAwesomeIcon className="merge-customer-arrow" icon={["far", "arrow-circle-down"]} size="2x" />
                <span className="merge-customer-title">{t("Current Customer")}</span>
                <GolferCard
                  email={state.customer?.email}
                  name={state.customer?.full_name}
                  memberCode={state.customer?.member_code}
                  customerType={state.customer?.customer_type}
                  phone={state.customer?.phone}
                />
              </div>
            ) : null}
          </Sheet>

          <Popup
            open={mergeState?.showWarning}
            type={"warning"}
            stacked
            title={t("Merge Customers?")}
            onCancel={() => setMergeState(prevState => ({ ...prevState, showWarning: false }))}
            cancelText={t("Cancel")}
            okText={t("Merge")}
            onOk={handleCustomerMerge}
            description={`${t("The account of")} ${
              mergeState?.selectedCustomer?.full_name
            } ${t("will be merged into the account of")}
            ${state?.customer?.full_name}. ${t("All gift cards, tickets, and accounts will be merged. This action cannot be undone. Are you sure you want to continue with this merge?")}
            `}
          />
          <Portal isMounted={auditModal.isOpen}>
            <Sheet
              open={auditModal.isOpen}
              size="small"
              closable
              onCancel={closeAuditModal}
              title={t("Customer Audit Trail")}
              hideCancelButton
            >
              <div>
                <ul>
                  Audit
                  {auditTrail.map((audit, index) => (
                    <>
                      <li key={audit.id}>
                        <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
                          <div className="audit-trail-note-container">
                            <div style={{ justifySelf: "center" }}>
                              <div className={`audit-trail-circle-${audit.action}`}>
                                {audit.action === "modified" ? (
                                  <FontAwesomeIcon
                                    icon={faCircleExclamation}
                                    className={`audit-trail-circle-${audit.action}-icon`}
                                  />
                                ) : audit.action === "deleted" ? (
                                  <FontAwesomeIcon
                                    icon={faTrash}
                                    className={`audit-trail-circle-${audit.action}-icon`}
                                  />
                                ) : audit.action === "disabled" ? (
                                  <FontAwesomeIcon icon={faBan} className={`audit-trail-circle-${audit.action}-icon`} />
                                ) : (
                                  <FontAwesomeIcon
                                    icon={faCircleCheck}
                                    className={`audit-trail-circle-${audit.action}-icon`}
                                  />
                                )}
                              </div>
                              {index < auditTrail.length - 1 ? <div className="bm-audit-trail-line"></div> : null}
                            </div>
                            <div style={{ marginLeft: "18px" }}>
                              <div className="audit-trail-title">{audit.title}</div>
                              <div className="audit-trail-note">{audit.description}</div>
                            </div>
                          </div>
                          <div className="audit-trail-date">
                            <div>{moment(audit.created_at as MomentInput).format("MMMM Do YYYY, h:mm a")}</div>
                            <div>{audit.user_id?.full_name}</div>
                          </div>
                        </div>
                      </li>
                    </>
                  ))}
                </ul>
              </div>
            </Sheet>
          </Portal>

          <Popup
            open={banModal?.isOpen}
            type={"warning"}
            stacked
            title={t("Ban Account")}
            onCancel={closeBanModal}
            okText={t("Ban")}
            onOk={() => handleBanCustomer("ban")}
            description={t("Are you sure you want to ban this account?")}
          >
            <br />
            <TextField
              value={banModal?.reason}
              onChange={e => updateBanModal({ reason: e?.target?.value })}
              label={t("Internal notes (optional)")}
              placeholder={t("Why are we banning the customer?")}
              maxLength={250}
              showCharacterCount
            />
          </Popup>

          <Popup
            open={unbanModal?.isOpen}
            type={"warning"}
            stacked
            title={t("Unban Account?")}
            onCancel={closeUnBanModal}
            okText={t("Unban")}
            onOk={() => handleBanCustomer("unban")}
            description={t(
              "Unbanning this customer will immediately restore their booking and payment privileges. Do you wish to continue?",
            )}
          />

          <Sheet
            open={banDetailsModal.isOpen}
            size="small"
            onCancel={closeBanDetailsModal}
            title={t("Ban Details")}
            okText={banDetailsModal?.edit ? t("Save Changes") : t("Edit Ban")}
            okDisabled={!authStore?.user?.permissions?.customers_ban_account}
            onOk={
              banDetailsModal?.edit
                ? () => handleBanCustomer("edit")
                : () => updateBanDetailsModal({ edit: true, reason: state?.customer?.banned_reason })
            }
          >
            <p className="customer-banned-text">
              Banned By:{" "}
              <span className="customer-banned-modal-text">
                {state?.customer?.banned_by_user_full_name} at{" "}
                {moment.utc(state?.customer?.banned_at).local()?.format("LLL")}
              </span>
            </p>
            <br />
            {banDetailsModal?.edit ? (
              <>
                <TextField
                  label={t("Internal notes:")}
                  value={banDetailsModal?.reason}
                  onChange={e => updateBanDetailsModal({ reason: e.target.value })}
                  placeholder={t("Ban reason...")}
                  maxLength={250}
                  showCharacterCount
                />
                <br />
              </>
            ) : state?.customer?.banned_reason ? (
              <>
                <p className="customer-banned-text">Internal notes: </p>
                <p className="customer-banned-modal-text">{state?.customer?.banned_reason}</p>
              </>
            ) : null}
          </Sheet>

        </>
      )}
    </>
  );
}
